import { useEffect, useMemo, useRef, useState } from "react";
import { buildFileUrl } from "@/lib/api";
import CrossfadeMedia from "@/components/CrossfadeMedia";

/**
 * High-performance live preview for TV cards.
 *
 * REAL-TIME SYNC: the preview no longer starts from index 0 on every page load.
 * It uses `playlist.updated_at` as a wall-clock anchor and computes which slot
 * the TV *should* be on right now. Refreshing the admin page snaps the preview
 * to the same position the Fire Stick is broadcasting in real life.
 *
 * Optimizations:
 *   1. Only renders when the card is visible (IntersectionObserver).
 *   2. Preloads the next image while the current one is showing.
 *   3. When the Fire Stick is online & reports a current item, snaps to it.
 */
export default function TVLivePreview({ playlist, media, nowPlaying }) {
  const items = useMemo(() => {
    if (!playlist || !media) return [];
    const map = new Map(media.map((m) => [m.id, m]));
    return (playlist.media_ids || []).map((id) => map.get(id)).filter(Boolean);
  }, [playlist, media]);

  const wrapRef = useRef(null);
  const [visible, setVisible] = useState(false);

  // Pause the entire preview when off-screen
  useEffect(() => {
    if (!wrapRef.current) return;
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => setVisible(e.isIntersecting)),
      { rootMargin: "200px" }
    );
    obs.observe(wrapRef.current);
    return () => obs.disconnect();
  }, []);

  // --- Wall-clock sync ----------------------------------------------------
  // Anchor: playlist.updated_at → ms since epoch. Falls back to created_at, then 0.
  const anchorMs = useMemo(() => {
    const raw = playlist?.updated_at || playlist?.created_at || 0;
    const t = raw ? new Date(raw).getTime() : 0;
    return Number.isFinite(t) && t > 0 ? t : 0;
  }, [playlist?.updated_at, playlist?.created_at]);

  const slotMs = Math.max(1, (playlist?.item_duration || 8)) * 1000;

  const computeSlot = () => {
    if (!items.length) return { index: 0, offsetMs: 0 };
    const elapsed = Math.max(0, Date.now() - anchorMs);
    const index = Math.floor(elapsed / slotMs) % items.length;
    const offsetMs = elapsed % slotMs;
    return { index, offsetMs };
  };

  const [slot, setSlot] = useState(() => computeSlot());

  // Re-sync the slot just before the next slot boundary. This is FAR cheaper
  // than ticking every 500ms — we sleep until the exact moment of transition
  // and only re-render then. With 8 TVs at 8s/slot that means ~1 tick/s
  // total instead of 16 ticks/s.
  useEffect(() => {
    if (!visible || items.length === 0) return;
    let timer;
    const schedule = () => {
      const elapsed = Math.max(0, Date.now() - anchorMs);
      const offsetMs = elapsed % slotMs;
      const wait = Math.max(50, slotMs - offsetMs + 30); // a tad after the boundary
      timer = window.setTimeout(() => {
        setSlot(computeSlot());
        schedule();
      }, wait);
    };
    setSlot(computeSlot());
    schedule();
    return () => {
      if (timer) window.clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, items.length, slotMs, anchorMs]);

  // Preload next image (skip videos)
  useEffect(() => {
    if (!visible || items.length < 2) return;
    const next = items[(slot.index + 1) % items.length];
    if (next && next.media_type === "image") {
      const img = new Image();
      img.src = buildFileUrl(next.id, "admin");
    }
  }, [visible, slot.index, items]);

  const isOnline = nowPlaying?.status === "online";
  const usingHeartbeat = isOnline && nowPlaying?.media;
  const current = (() => {
    if (usingHeartbeat) return nowPlaying.media;
    if (items.length) return items[slot.index % items.length];
    return null;
  })();

  // For videos, seek to the wall-clock offset (real-time mirror of the TV).
  // When the TV's heartbeat is feeding us the current media directly we have no
  // exact playhead, so seek to a small offset based on how long since the heartbeat.
  const startOffsetMs = (() => {
    if (!current) return 0;
    if (usingHeartbeat) {
      // Estimate how much of the current item the TV has already played by
      // measuring time since its last_heartbeat. Caps to a single slot.
      const lhb = nowPlaying?.last_heartbeat ? new Date(nowPlaying.last_heartbeat).getTime() : 0;
      if (!lhb) return 0;
      const since = Math.max(0, Date.now() - lhb);
      return Math.min(since, slotMs);
    }
    return slot.offsetMs;
  })();

  return (
    <div ref={wrapRef} className="relative w-full h-full">
      {visible && current ? (
        <CrossfadeMedia
          item={current}
          buildSrc={(it) => buildFileUrl(it.id, "admin")}
          duration={500}
          startOffsetMs={startOffsetMs}
          onVideoEnded={() => {
            // Let the wall-clock ticker advance naturally; nothing to do.
          }}
        />
      ) : !current ? (
        <div className="absolute inset-0 grid place-items-center text-xs text-white/40 uppercase tracking-[0.25em]">
          {playlist ? "Playlist is empty" : "No playlist assigned"}
        </div>
      ) : null}
    </div>
  );
}
